import { faq } from '@getpopsure/private-constants';
import { Country } from '@getpopsure/public-models';
import {
  BottomOrRegularModal,
  Button,
  InformationBox,
  Input,
} from '@popsure/dirty-swan';
import { CountrySelectStandalone } from 'components/CountrySelectStandalone/CountrySelectStandalone';
import dayjs from 'dayjs';
import { IntercomLoaderWrapper } from 'features/intercomLauncher';
import { Fragment, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { TFunction, Trans, useSafeTranslation } from 'shared/i18n';

import styles from '../style.module.scss';
import {
  GenericCancellationReason,
  GenericCancellationReasonsId,
  ViewProps,
} from '../types';

export const isCancellationAutomated = (startDate?: string) => {
  if (!startDate) {
    return false;
  }
  return dayjs().diff(dayjs(startDate), 'years') >= 1;
};

export const CancelDentalModal = (props: ViewProps) => {
  const { startDate } = props;
  if (isCancellationAutomated(startDate)) {
    return <CancelDentalAutomatedModal {...props} />;
  }
  return <CancelDentalManualModal {...props} />;
};

const getCancellationReasons = (t: TFunction): GenericCancellationReason[] => {
  const reasons: GenericCancellationReason[] = [
    {
      id: 'LEFT_COUNTRY',
      title: t(
        'dental.cancelModal.automatedCancellation.reason.leftCountry.title',
        'I will leave the country'
      ),
    },
    {
      id: 'OTHER',
      title: t(
        'dental.cancelModal.automatedCancellation.reason.other.title',
        'Other'
      ),
    },
  ];

  return reasons;
};

export const canCancel = (
  cancelationReason?: GenericCancellationReasonsId,
  additionalInfo?: string
): boolean => {
  if (cancelationReason === 'OTHER' || cancelationReason === 'LEFT_COUNTRY') {
    return additionalInfo !== undefined && additionalInfo.length > 0;
  }

  return cancelationReason !== undefined;
};

const CancelDentalManualModal = ({
  onCloseModal,
  onCancelPolicy,
  isCancelling,
  cancellingError,
  isOpen,
}: ViewProps) => {
  const { t } = useSafeTranslation();

  const [customCancellationReason, setCustomCancellationReason] = useState('');
  const [selectedCancellationReason, setSelectedCancellationReason] = useState<
    GenericCancellationReasonsId | undefined
  >();

  const [newCountry, setNewCountry] = useState<Country | undefined>();

  const getAdditionalInfo = (): string | undefined => {
    if (selectedCancellationReason === 'OTHER') {
      return customCancellationReason;
    }

    if (selectedCancellationReason === 'LEFT_COUNTRY') {
      return newCountry?.name;
    }

    return undefined;
  };

  const additionalInfo = getAdditionalInfo();

  const handleCancelPolicy = () => {
    const successMessage = t(
      'dental.cancelModal.automatedCancellation.reason.receivedRequest',
      'We have received your cancellation request'
    );

    onCancelPolicy(selectedCancellationReason, additionalInfo, successMessage);
  };

  const valid = canCancel(selectedCancellationReason, additionalInfo);

  return (
    <BottomOrRegularModal
      title={t('dental.cancelModal.manualCancellation.title', 'Cancel policy')}
      isOpen={isOpen}
      onClose={onCloseModal}
    >
      <div
        className={`mt24 ${styles.container}`}
        data-testid="cancel-modal-dental-manual"
      >
        {getCancellationReasons(t).map(({ id, title: reasonTitle }) => (
          <Fragment key={id}>
            <label className="p-p mt8 d-block" htmlFor={id}>
              <input
                id={id}
                value={id}
                type="radio"
                name="cancellationReason"
                onChange={() => {
                  setSelectedCancellationReason(id);
                }}
                checked={id === selectedCancellationReason}
              />
              &nbsp;{reasonTitle}
            </label>

            {id === 'LEFT_COUNTRY' && (
              <AnimateHeight
                duration={300}
                height={
                  selectedCancellationReason === 'LEFT_COUNTRY' ? 'auto' : 0
                }
              >
                <p className="p-p my8">
                  {t(
                    'myPolicies.cancelModal.automatedCancellation.reason.leftCountry.input.label',
                    'Which country are you moving to?'
                  )}
                </p>
                <CountrySelectStandalone
                  country={newCountry}
                  setCountry={setNewCountry}
                />
              </AnimateHeight>
            )}

            {id === 'OTHER' && (
              <AnimateHeight
                duration={300}
                height={selectedCancellationReason === 'OTHER' ? 'auto' : 0}
              >
                <Input
                  className="mt8 wmx5"
                  placeholder={t(
                    'myPolicies.cancelModal.automatedCancellation.reason.other.input.placeholder',
                    'Please type the reason for cancellation'
                  )}
                  value={customCancellationReason ?? ''}
                  onChange={(e) => {
                    setCustomCancellationReason(e.target.value);
                  }}
                />
              </AnimateHeight>
            )}
          </Fragment>
        ))}
        <div className="p-p mt24">
          <Trans key="dental.cancelModal.manualCancellation.description.body">
            <p className="fw-bold mt8 mb8">
              If you are upgrading your dental insurance, you do not need to put
              in a cancel request - we will take care of that for you!
            </p>
            Keep in mind that receiving a claim payout may change your{' '}
            <a
              href={faq.dentalClaimSubmit}
              className="p-a"
              target="_blank"
              rel="noreferrer"
            >
              cancellation options
            </a>
            .
          </Trans>
        </div>
        <div className={`d-flex f-wrap mt24 ${styles['button-container']}`}>
          <Button
            className="wmn3"
            loading={isCancelling}
            disabled={!valid || isCancelling}
            onClick={handleCancelPolicy}
            type="button"
          >
            {t(
              'dental.cancelModal.manualCancellation.cancelButton.caption',
              'Request to cancel'
            )}
          </Button>
          <ContactUsButton />
        </div>
        {cancellingError && (
          <InformationBox variant="error">{cancellingError}</InformationBox>
        )}
      </div>
    </BottomOrRegularModal>
  );
};

const CancelDentalAutomatedModal = ({
  onCloseModal,
  onCancelPolicy,
  isCancelling,
  cancellingError,
  isOpen,
  activeUntil,
}: ViewProps) => {
  const { t } = useSafeTranslation();

  const [customCancellationReason, setCustomCancellationReason] = useState('');
  const [selectedCancellationReason, setSelectedCancellationReason] = useState<
    GenericCancellationReasonsId | undefined
  >();

  const [newCountry, setNewCountry] = useState<Country | undefined>();

  const getAdditionalInfo = (): string | undefined => {
    if (selectedCancellationReason === 'OTHER') {
      return customCancellationReason;
    }

    if (selectedCancellationReason === 'LEFT_COUNTRY') {
      return newCountry?.name;
    }

    return undefined;
  };

  const additionalInfo = getAdditionalInfo();

  const handleCancelPolicy = () => {
    const successMessage = t(
      'dental.cancelModal.automatedCancellation.reason.successMessage',
      'Your policy was successfully cancelled'
    );

    onCancelPolicy(selectedCancellationReason, additionalInfo, successMessage);
  };

  const valid = canCancel(selectedCancellationReason, additionalInfo);

  return (
    <BottomOrRegularModal
      title={t(
        'dental.cancelModal.automatedCancellation.title',
        'Cancel policy'
      )}
      isOpen={isOpen}
      onClose={onCloseModal}
    >
      <div
        className={`mt24 ${styles.container}`}
        data-testid="cancel-modal-dental-auto"
      >
        {getCancellationReasons(t).map(({ id, title: reasonTitle }) => (
          <Fragment key={id}>
            <label className="p-p mt8 d-block" htmlFor={id}>
              <input
                id={id}
                value={id}
                type="radio"
                name="cancellationReason"
                onChange={() => {
                  setSelectedCancellationReason(id);
                }}
                checked={id === selectedCancellationReason}
              />
              &nbsp;{reasonTitle}
            </label>

            {id === 'LEFT_COUNTRY' && (
              <AnimateHeight
                duration={300}
                height={
                  selectedCancellationReason === 'LEFT_COUNTRY' ? 'auto' : 0
                }
              >
                <p className="p-p my8">
                  {t(
                    'myPolicies.cancelModal.automatedCancellation.reason.leftCountry.input.label',
                    'Which country are you moving to?'
                  )}
                </p>
                <CountrySelectStandalone
                  country={newCountry}
                  setCountry={setNewCountry}
                />
              </AnimateHeight>
            )}

            {id === 'OTHER' && (
              <AnimateHeight
                duration={300}
                height={selectedCancellationReason === 'OTHER' ? 'auto' : 0}
              >
                <Input
                  className="mt8 wmx5"
                  placeholder={t(
                    'myPolicies.cancelModal.automatedCancellation.reason.other.input.placeholder',
                    'Please type the reason for cancellation'
                  )}
                  value={customCancellationReason ?? ''}
                  onChange={(e) => {
                    setCustomCancellationReason(e.target.value);
                  }}
                />
              </AnimateHeight>
            )}
          </Fragment>
        ))}
        <p className="p-p mt24">
          {t(
            'dental.cancelModal.automatedCancellation.description.2',
            'You can reach out to us if you have a preferred cancellation date.'
          )}
        </p>
        {activeUntil && (
          <Trans i18nKey="dental.cancelModal.automatedCancellation.activeUntil.description">
            <p className="p-notice--warning mt24 p-p">
              The cancellation will be effective at the end of your next billing
              period on{' '}
              <b className="fw-bold">
                {{ endDate: dayjs(activeUntil).format('DD MMM YYYY') }}.
              </b>
            </p>
          </Trans>
        )}
        <div className={`d-flex f-wrap mt24 ${styles['button-container']}`}>
          <Button
            className="wmn3"
            loading={isCancelling}
            disabled={!valid || isCancelling}
            onClick={handleCancelPolicy}
            type="button"
          >
            {t(
              'dental.cancelModal.automatedCancellation.cancelButton.caption',
              'Cancel policy'
            )}
          </Button>
          <ContactUsButton />
        </div>
        {cancellingError && (
          <InformationBox variant="error">{cancellingError}</InformationBox>
        )}
      </div>
    </BottomOrRegularModal>
  );
};

export const ContactUsButton = () => {
  const { t } = useSafeTranslation();
  return (
    <IntercomLoaderWrapper
      render={({ loading, handleClick }) => {
        if (loading) {
          return (
            <Button loading variant="filledGray" className="wmn2" type="button">
              Loading
            </Button>
          );
        }

        return (
          <Button
            className="wmn2"
            type="button"
            onClick={() => {
              handleClick();
              if (window.Intercom) {
                window.Intercom('show');
              }
            }}
            variant="textColor"
          >
            {t(
              'dental.cancelModal.automatedCancellation.contactUs.caption',
              'Contact us'
            )}
          </Button>
        );
      }}
    />
  );
};
